/**
 * Wishlist (Wunschliste)
 *
 * - Public heart toggle on product cards / product page (wishlist-button)
 * - Header / dock icon with count (wishlist-link, wishlist-count)
 * - Right-hand drawer based on the cart drawer (wishlist-drawer)
 * - Guests get a "please register" popup (#WishlistLoginModal)
 *
 * Items are stored in localStorage per customer id:
 *   Concept:wishlist:<customerId> => [{ id: <productId>, variant: <variantId> }]
 */
(() => {
  const EVENTS = {
    updated: 'wishlist:updated'
  };

  const strings = theme.wishlistStrings || {};

  const store = {
    get customer() {
      return theme.customer || { loggedIn: false, id: null };
    },

    get enabled() {
      return this.customer.loggedIn === true && theme.config.hasLocalStorage;
    },

    get key() {
      return `${theme.settings.themeName}:wishlist:${this.customer.id || 'guest'}`;
    },

    read() {
      if (!this.enabled) return [];

      try {
        const parsed = JSON.parse(window.localStorage.getItem(this.key) || '[]');
        if (!Array.isArray(parsed)) return [];

        return parsed
          .map((item) => {
            if (typeof item === 'number') return { id: item, variant: null };
            if (item && typeof item.id !== 'undefined') {
              return { id: parseInt(item.id, 10), variant: item.variant ? parseInt(item.variant, 10) : null };
            }
            return null;
          })
          .filter((item) => item && !Number.isNaN(item.id));
      }
      catch (e) {
        return [];
      }
    },

    write(items) {
      if (!this.enabled) return;

      try {
        window.localStorage.setItem(this.key, JSON.stringify(items));
      }
      catch (e) {
        // Safari private mode: silently fail
      }

      this.notify(items);
    },

    notify(items = this.read()) {
      document.dispatchEvent(new CustomEvent(EVENTS.updated, { detail: { items } }));
    },

    get items() {
      return this.read();
    },

    get count() {
      return this.read().length;
    },

    has(productId) {
      productId = parseInt(productId, 10);
      return this.read().some((item) => item.id === productId);
    },

    get(productId) {
      productId = parseInt(productId, 10);
      return this.read().find((item) => item.id === productId) || null;
    },

    add(productId, variantId = null) {
      productId = parseInt(productId, 10);
      variantId = variantId ? parseInt(variantId, 10) : null;

      const items = this.read().filter((item) => item.id !== productId);
      items.unshift({ id: productId, variant: variantId });
      this.write(items);
    },

    remove(productId) {
      productId = parseInt(productId, 10);
      this.write(this.read().filter((item) => item.id !== productId));
    },

    removeMany(productIds) {
      const ids = new Set(productIds.map((id) => parseInt(id, 10)));
      this.write(this.read().filter((item) => !ids.has(item.id)));
    },

    toggle(productId, variantId = null) {
      if (this.has(productId)) {
        this.remove(productId);
        return false;
      }

      this.add(productId, variantId);
      return true;
    }
  };

  theme.wishlist = store;

  const getDrawer = () => document.getElementById('WishlistDrawer');
  const getLoginModal = () => document.getElementById('WishlistLoginModal');
  const getCartDrawer = () => document.querySelector('cart-drawer');

  /**
   * Opens either the wishlist drawer (customer) or the register popup (guest).
   */
  const openWishlist = (activeElement = null) => {
    if (store.enabled) {
      getDrawer()?.show(activeElement);
    }
    else {
      getLoginModal()?.show(activeElement);
    }
  };

  /* ------------------------------------------------------------------ */
  /* Heart toggle                                                        */
  /* ------------------------------------------------------------------ */

  if (!customElements.get('wishlist-button')) {
    customElements.define(
      'wishlist-button',
      class WishlistButton extends HTMLElement {
        constructor() {
          super();

          this.onUpdatedListener = this.sync.bind(this);
        }

        get productId() {
          return this.getAttribute('data-product-id');
        }

        get variantId() {
          return this.getAttribute('data-variant-id');
        }

        get button() {
          return this._button = this._button || this.querySelector('button');
        }

        get label() {
          return this.querySelector('[data-label]');
        }

        connectedCallback() {
          this.button?.addEventListener('click', this.onClick.bind(this));
          document.addEventListener(EVENTS.updated, this.onUpdatedListener);

          // Product page: follow the selected variant
          const form = this.closest('form[is="product-form"]') || this.closest('.product-form-wrapper')?.querySelector('form[is="product-form"]');
          if (form) {
            this.variantChangeUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, (event) => {
              const data = event?.data;
              if (data?.variant?.id && String(data.productId) === String(this.productId)) {
                this.setAttribute('data-variant-id', data.variant.id);
              }
            });
          }

          this.sync();
        }

        disconnectedCallback() {
          document.removeEventListener(EVENTS.updated, this.onUpdatedListener);
          this.variantChangeUnsubscriber?.();
        }

        sync() {
          const active = store.has(this.productId);

          this.classList.toggle('is-active', active);
          if (this.button) {
            this.button.setAttribute('aria-pressed', active ? 'true' : 'false');
            const label = active ? this.button.getAttribute('data-label-remove') : this.button.getAttribute('data-label-add');
            if (label) {
              this.button.setAttribute('aria-label', label);
              if (this.label) this.label.textContent = label;
            }
          }
        }

        onClick(event) {
          event.preventDefault();
          event.stopPropagation();

          if (!store.enabled) {
            getLoginModal()?.show(this.button);
            return;
          }

          const added = store.toggle(this.productId, this.variantId);

          if (added) {
            this.classList.add('is-animating');
            setTimeout(() => this.classList.remove('is-animating'), 600);
            getDrawer()?.show(this.button);
          }
        }
      }
    );
  }

  /* ------------------------------------------------------------------ */
  /* Header / dock link                                                  */
  /* ------------------------------------------------------------------ */

  if (!customElements.get('wishlist-link')) {
    const BaseLink = typeof MagnetLink !== 'undefined' ? MagnetLink : HTMLAnchorElement;

    customElements.define(
      'wishlist-link',
      class WishlistLink extends BaseLink {
        constructor() {
          super();

          this.addEventListener('click', (event) => {
            event.preventDefault();
            openWishlist(this);
          });
        }
      }, { extends: 'a' }
    );
  }

  /* ------------------------------------------------------------------ */
  /* Count badge                                                         */
  /* ------------------------------------------------------------------ */

  if (!customElements.get('wishlist-count')) {
    customElements.define(
      'wishlist-count',
      class WishlistCount extends HTMLElement {
        constructor() {
          super();

          this.onUpdatedListener = this.sync.bind(this);
        }

        connectedCallback() {
          document.addEventListener(EVENTS.updated, this.onUpdatedListener);
          this.sync();
        }

        disconnectedCallback() {
          document.removeEventListener(EVENTS.updated, this.onUpdatedListener);
        }

        sync() {
          const count = store.count;
          this.textContent = count;
          this.hidden = count === 0;

          if (strings.count) {
            this.setAttribute('aria-label', strings.count.replace('[count]', count));
          }
        }
      }
    );
  }

  /* ------------------------------------------------------------------ */
  /* Drawer                                                              */
  /* ------------------------------------------------------------------ */

  if (!customElements.get('wishlist-drawer') && typeof DrawerElement !== 'undefined') {
    customElements.define(
      'wishlist-drawer',
      class WishlistDrawer extends DrawerElement {
        constructor() {
          super();

          this.dirty = true;
          this.loading = false;

          this.onUpdatedListener = this.onUpdated.bind(this);
          this.onClickListener = this.onClick.bind(this);
          this.onChangeListener = this.onChange.bind(this);
        }

        get shouldAppendToBody() {
          return false;
        }

        get sectionId() {
          return this.getAttribute('data-section-id');
        }

        get url() {
          return this.getAttribute('data-url');
        }

        get itemsElement() {
          return this.querySelector('wishlist-items');
        }

        get list() {
          return this.itemsElement?.querySelector('ul');
        }

        get rows() {
          return Array.from(this.querySelectorAll('.wishlist-item'));
        }

        get checkboxes() {
          return this.rows.map((row) => row.querySelector('input[type="checkbox"]')).filter(Boolean);
        }

        get selectedRows() {
          return this.rows.filter((row) => {
            const checkbox = row.querySelector('input[type="checkbox"]');
            return checkbox && checkbox.checked && !checkbox.disabled;
          });
        }

        get emptyElement() {
          return this.querySelector('[data-empty]');
        }

        get itemsWrapper() {
          return this.querySelector('[data-items-wrapper]');
        }

        get footer() {
          return this.querySelector('[data-footer]');
        }

        get errorElement() {
          return this.querySelector('[data-error]');
        }

        get selectAll() {
          return this.querySelector('[data-select-all]');
        }

        get addSelectedButton() {
          return this.querySelector('[data-add-selected]');
        }

        connectedCallback() {
          super.connectedCallback();

          document.addEventListener(EVENTS.updated, this.onUpdatedListener);
          this.addEventListener('click', this.onClickListener);
          this.addEventListener('change', this.onChangeListener);
        }

        disconnectedCallback() {
          super.disconnectedCallback();

          document.removeEventListener(EVENTS.updated, this.onUpdatedListener);
          this.removeEventListener('click', this.onClickListener);
          this.removeEventListener('change', this.onChangeListener);
        }

        beforeShow() {
          super.beforeShow();

          if (this.dirty) {
            this.load();
          }
        }

        onUpdated(event) {
          const items = event.detail.items || [];
          const ids = new Set(items.map((item) => item.id));

          if (!this.open) {
            this.dirty = true;
            return;
          }

          // Drawer is open: remove rows that are no longer on the list, reload if something new was added
          let needsReload = false;
          this.rows.forEach((row) => {
            if (!ids.has(parseInt(row.getAttribute('data-product-id'), 10))) {
              row.remove();
            }
          });

          const rowIds = new Set(this.rows.map((row) => parseInt(row.getAttribute('data-product-id'), 10)));
          items.forEach((item) => {
            if (!rowIds.has(item.id)) needsReload = true;
          });

          if (needsReload) {
            this.load();
          }
          else {
            this.updateState();
          }
        }

        setError(message = '') {
          if (!this.errorElement) return;

          this.errorElement.textContent = message;
          this.errorElement.hidden = message.length === 0;
        }

        async load() {
          if (this.loading) return;

          const items = store.items;
          this.setError();

          if (items.length === 0) {
            this.renderEmpty();
            this.dirty = false;
            return;
          }

          this.loading = true;
          this.itemsElement?.classList.add('is-loading');

          try {
            const query = items.map((item) => `id:${item.id}`).join(' OR ');
            const response = await fetch(this.url + encodeURIComponent(query));
            const responseText = await response.text();

            const parsed = new DOMParser().parseFromString(responseText, 'text/html');
            const remoteItems = parsed.querySelector('wishlist-items');

            if (!remoteItems || !this.itemsElement) {
              throw new Error('Wishlist: unable to render items');
            }

            this.itemsElement.innerHTML = remoteItems.innerHTML;
            this.postProcess(items);
          }
          catch (error) {
            console.error(error);
            this.setError(strings.error || 'Something went wrong. Please try again.');
          }
          finally {
            this.loading = false;
            this.itemsElement?.classList.remove('is-loading');
            this.dirty = false;
          }
        }

        /**
         * Sort rows in wishlist order, apply the saved variant and drop products that no longer exist.
         */
        postProcess(items) {
          const list = this.list;
          if (!list) return;

          const rowsById = new Map();
          this.rows.forEach((row) => rowsById.set(parseInt(row.getAttribute('data-product-id'), 10), row));

          const missing = [];
          items.forEach((item) => {
            const row = rowsById.get(item.id);
            if (!row) {
              missing.push(item.id);
              return;
            }

            if (item.variant) {
              this.applyVariant(row, item.variant);
            }
            list.appendChild(row);
          });

          // Products that were deleted / unpublished: clean up silently
          if (missing.length > 0 && missing.length < items.length) {
            const cleaned = items.filter((item) => !missing.includes(item.id));
            try {
              window.localStorage.setItem(store.key, JSON.stringify(cleaned));
            }
            catch (e) { /* noop */ }
            store.notify(cleaned);
          }

          this.updateState();
        }

        applyVariant(row, variantId) {
          const script = row.querySelector('script[data-variants]');
          if (!script) return;

          let variants = [];
          try {
            variants = JSON.parse(script.textContent);
          }
          catch (e) {
            return;
          }

          const variant = variants.find((v) => v.id === variantId);
          if (!variant || parseInt(row.getAttribute('data-variant-id'), 10) === variant.id) return;

          row.setAttribute('data-variant-id', variant.id);
          row.toggleAttribute('data-available', variant.available);

          const checkbox = row.querySelector('input[type="checkbox"]');
          if (checkbox) {
            checkbox.value = variant.id;
            checkbox.disabled = !variant.available;
            if (!variant.available) checkbox.checked = false;
          }

          const addButton = row.querySelector('[data-add]');
          if (addButton) {
            addButton.setAttribute('data-variant-id', variant.id);
            addButton.disabled = !variant.available;
            const label = addButton.querySelector('[data-add-label]');
            if (label) {
              label.textContent = variant.available ? addButton.getAttribute('data-label-add') : addButton.getAttribute('data-label-sold-out');
            }
          }

          const title = row.querySelector('[data-variant-title]');
          if (title) {
            title.textContent = variant.title;
            title.hidden = false;
          }

          const price = row.querySelector('[data-price]');
          if (price && theme.Currency) {
            const format = theme.settings.currencyCodeEnabled ? theme.settings.moneyWithCurrencyFormat : theme.settings.moneyFormat;
            const onSale = variant.compare_at_price > variant.price;
            price.classList.toggle('price--on-sale', onSale);
            price.innerHTML = onSale
              ? `<span class="price__sale inline-flex items-center relative">${theme.Currency.formatMoney(variant.compare_at_price, format)}</span><span class="price__regular whitespace-nowrap">${theme.Currency.formatMoney(variant.price, format)}</span>`
              : theme.Currency.formatMoney(variant.price, format);
          }

          const image = row.querySelector('[data-media] img');
          if (image && variant.image) {
            image.removeAttribute('srcset');
            image.removeAttribute('sizes');
            image.src = variant.image;
          }
        }

        renderEmpty() {
          if (this.emptyElement) this.emptyElement.hidden = false;
          if (this.itemsWrapper) this.itemsWrapper.hidden = true;
          if (this.footer) this.footer.hidden = true;
        }

        updateState() {
          const rows = this.rows;

          if (rows.length === 0) {
            this.renderEmpty();
            return;
          }

          if (this.emptyElement) this.emptyElement.hidden = true;
          if (this.itemsWrapper) this.itemsWrapper.hidden = false;
          if (this.footer) this.footer.hidden = false;

          const selectable = this.checkboxes.filter((checkbox) => !checkbox.disabled);
          const selected = this.selectedRows;

          if (this.selectAll) {
            this.selectAll.disabled = selectable.length === 0;
            this.selectAll.checked = selectable.length > 0 && selected.length === selectable.length;
            this.selectAll.indeterminate = selected.length > 0 && selected.length < selectable.length;
          }

          const info = this.querySelector('[data-selected-info]');
          if (info) {
            info.textContent = selected.length > 0 && strings.selected
              ? strings.selected.replace('[count]', selected.length)
              : '';
          }

          if (this.addSelectedButton) {
            this.addSelectedButton.disabled = selected.length === 0;
            const label = this.addSelectedButton.querySelector('[data-add-selected-label]');
            if (label) {
              label.textContent = selected.length > 0
                ? (label.getAttribute('data-label-template') || '').replace('[count]', selected.length)
                : label.getAttribute('data-label-empty');
            }
          }

          rows.forEach((row) => {
            const checkbox = row.querySelector('input[type="checkbox"]');
            row.classList.toggle('is-selected', !!(checkbox && checkbox.checked));
          });
        }

        onChange(event) {
          const target = event.target;

          if (target.matches('[data-select-all]')) {
            this.checkboxes.forEach((checkbox) => {
              if (!checkbox.disabled) checkbox.checked = target.checked;
            });
          }

          this.updateState();
        }

        onClick(event) {
          const removeButton = event.target.closest('[data-remove]');
          if (removeButton) {
            event.preventDefault();
            const row = removeButton.closest('.wishlist-item');
            if (row) store.remove(row.getAttribute('data-product-id'));
            return;
          }

          const addButton = event.target.closest('[data-add]');
          if (addButton) {
            event.preventDefault();
            const row = addButton.closest('.wishlist-item');
            const variantId = addButton.getAttribute('data-variant-id');
            if (variantId) {
              this.addToCart([{ id: parseInt(variantId, 10), quantity: 1 }], addButton, row ? [row.getAttribute('data-product-id')] : []);
            }
            return;
          }

          const addSelectedButton = event.target.closest('[data-add-selected]');
          if (addSelectedButton) {
            event.preventDefault();
            const rows = this.selectedRows.filter((row) => !Number.isNaN(parseInt(row.getAttribute('data-variant-id'), 10)));
            const items = rows.map((row) => ({ id: parseInt(row.getAttribute('data-variant-id'), 10), quantity: 1 }));

            if (items.length > 0) {
              this.addToCart(items, addSelectedButton, rows.map((row) => row.getAttribute('data-product-id')));
            }
          }
        }

        /**
         * @param {Array<{id:number, quantity:number}>} items - variants to add
         * @param {HTMLElement|null} button - button that triggered the action
         * @param {Array<string>} productIds - products to remove from the wishlist once added
         */
        async addToCart(items, button = null, productIds = []) {
          if (items.length === 0) return;

          this.setError();

          // Cart page / cart type "page": use a classic form post like the theme does
          if (!Shopify.designMode && (document.body.classList.contains('template-cart') || theme.settings.cartType === 'page')) {
            if (productIds.length > 0) store.removeMany(productIds);
            theme.utils.postLink2(theme.routes.cart_add_url, { parameters: { items } });
            return;
          }

          if (button?.hasAttribute('aria-disabled')) return;

          const buttons = [button, this.addSelectedButton].filter(Boolean);
          buttons.forEach((element) => {
            element.setAttribute('aria-disabled', 'true');
            element.setAttribute('aria-busy', 'true');
          });
          this.classList.add('is-adding');

          let sectionsToBundle = [];
          document.documentElement.dispatchEvent(new CustomEvent('cart:bundled-sections', { bubbles: true, detail: { sections: sectionsToBundle } }));

          const body = JSON.stringify({
            items,
            sections: sectionsToBundle,
            sections_url: window.location.pathname
          });

          try {
            const response = await fetch(theme.routes.cart_add_url, { ...theme.utils.fetchConfig('javascript'), body });
            const parsedState = await response.json();

            if (parsedState.status) {
              this.setError(parsedState.description || parsedState.message || strings.error);
              document.dispatchEvent(new CustomEvent('ajaxProduct:error', {
                detail: { errorMessage: parsedState.description }
              }));
              return;
            }

            const cartJson = await (await fetch(theme.routes.cart_url, { ...theme.utils.fetchConfig('json', 'GET') })).json();
            cartJson['sections'] = parsedState['sections'];

            theme.pubsub.publish(theme.pubsub.PUB_SUB_EVENTS.cartUpdate, { source: 'wishlist-drawer', cart: cartJson });
            document.dispatchEvent(new CustomEvent('ajaxProduct:added', {
              detail: { product: parsedState }
            }));

            // Added products leave the wishlist (removes the rows via wishlist:updated)
            if (productIds.length > 0) {
              store.removeMany(productIds);
            }
            this.checkboxes.forEach((checkbox) => { checkbox.checked = false; });
            this.updateState();

            const cartDrawer = getCartDrawer();
            if (cartDrawer) {
              await (this.hide() || Promise.resolve());
              cartDrawer.show(button);
            }
          }
          catch (error) {
            console.error(error);
            this.setError(strings.error || 'Something went wrong. Please try again.');
          }
          finally {
            buttons.forEach((element) => {
              element.removeAttribute('aria-busy');
              element.removeAttribute('aria-disabled');
            });
            this.classList.remove('is-adding');
          }
        }
      }
    );
  }

  /* ------------------------------------------------------------------ */
  /* Initial sync                                                        */
  /* ------------------------------------------------------------------ */

  // Hearts and counters are rendered server-side in the "empty" state; sync them once the script runs
  store.notify();

  // Keep multiple tabs in sync
  window.addEventListener('storage', (event) => {
    if (event.key === store.key) {
      store.notify();
    }
  });
})();
